import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Code, Database, BookOpen, ArrowRight, Lock } from 'lucide-react';
import { Button } from '../ui/button';

interface PersonalDevelopmentProps {
  user?: any;
}

export default function PersonalDevelopment({ user }: PersonalDevelopmentProps) {
  const navigate = useNavigate();

  const handleAccessClick = () => {
    if (!user) {
      sessionStorage.setItem('returnTo', '/cursos');
      navigate('/login');
    } else {
      navigate('/cursos');
    }
  };

  return (
    <section id="desarrollo-personal" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Desarrollo Personal
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Accedé a ejercicios prácticos, consolas interactivas y recursos exclusivos para potenciar tu carrera en Data Engineering
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Database className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">SQL Práctico</h3>
            <p className="text-gray-600 mb-4">
              Ejercicios interactivos con base de datos reales. Practicá queries, analizá resultados y mejorá tus habilidades.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow"
          >
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Code className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Python</h3>
            <p className="text-gray-600 mb-4">
              Editor interactivo con ejercicios prácticos y casos de test. Desarrollá habilidades de programación desde cero.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <BookOpen className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Fundamentos</h3>
            <p className="text-gray-600 mb-4">
              Recursos y guías sobre fundamentos de Data Engineering. Conceptos clave explicados de forma práctica.
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center"
        >
          {user ? (
            <Button
              onClick={handleAccessClick}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Acceder al Área de Miembros
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <div className="bg-white rounded-lg p-8 shadow-lg max-w-md mx-auto">
              <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Acceso Requerido
              </h3>
              <p className="text-gray-600 mb-6">
                Iniciá sesión para acceder a todos los recursos y ejercicios prácticos.
              </p>
              <Button
                onClick={handleAccessClick}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg rounded-lg transition-all duration-300 transform hover:scale-105 w-full"
              >
                Iniciar Sesión
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

